/**
 * The people-directory slice of the wire — Task T2-B (pillar: manage people),
 * consuming `GET /employees`, `GET /employees/{employeeId}` and
 * `GET /employees/departments`, all already built and tested against
 * `openapi.yaml`'s `Employees` tag (Task T2-A). Hand-written to mirror the contract,
 * the same discipline `presence.ts`'s own header states: transcribe what the screen
 * reads, not the whole document, and this task does not touch `openapi.yaml`.
 *
 * A field the contract marks `nullable` is `T | null` here, never omitted — the
 * server sends the key (`exactOptionalPropertyTypes` is on, and `presence.ts`'s own
 * header gives the reason).
 *
 * ── The permission/scope contract, verbatim from `openapi.yaml` ─────────────
 *
 * All three endpoints share `x-wp-permissions: [workforce.view_self,
 * workforce.view_department, workforce.view_workspace]` and `x-wp-scope: resolved`
 * — the same `resolved` ladder (self ⊂ department ⊂ company) `GET /presence` uses,
 * with no new permission code. Concretely:
 *
 *  - `view_self` alone → the caller sees exactly **one row**: themself.
 *  - a manager's `view_department` → their whole subtree.
 *  - `view_workspace` → the company.
 *
 * `nav/menu.ts`'s own item note repeats this for the menu projection; it is stated
 * here too because a caller of this module — `EmployeePicker`, `PeopleScreen` — has
 * no client-side narrowing to add on top: the server's resolved scope is the whole
 * answer, and rendering fewer rows than it returned would be inventing a second,
 * disagreeing authorisation path (`nav/menu.ts`'s own boxed warning against exactly
 * that).
 *
 * ── No productivity, no payroll, no device ───────────────────────────────────
 *
 * `EmployeeDirectoryRow`'s own description: "One `wp_employees` row, mirror-only.
 * No payroll, no productivity, no device, no timezone — those stay on their own
 * surfaces." Nothing below adds a field the contract does not publish.
 */

import { qs, type QueryValue } from './paths';

/** `wp_employees.role` — the same three values `PresenceRow`-adjacent screens use. */
export const EMPLOYEE_ROLES = ['ceo', 'manager', 'employee'] as const;
export type EmployeeRole = (typeof EMPLOYEE_ROLES)[number];

/**
 * `listEmployees`'s own `status` filter. `active` is the endpoint's default
 * (`is_active = true AND offboarded_at IS NULL`); `offboarded` is
 * `offboarded_at IS NOT NULL OR is_active = false`; `all` narrows nothing.
 */
export const EMPLOYEE_STATUS_FILTERS = ['active', 'offboarded', 'all'] as const;
export type EmployeeStatusFilter = (typeof EMPLOYEE_STATUS_FILTERS)[number];

/**
 * `listEmployees`'s own `sort` enum. `full_name` (default): `full_name` ASC, then
 * `id` ASC. `department`: `department_name` ASC NULLS LAST, then `full_name`.
 * `synced_at`: DESC.
 */
export const EMPLOYEE_SORTS = ['full_name', 'department', 'synced_at'] as const;
export type EmployeeSort = (typeof EMPLOYEE_SORTS)[number];

/**
 * `EmployeeDirectoryRow` of `openapi.yaml`. `required` lists every member below —
 * there is no optional field on this schema, only nullable ones.
 */
export interface EmployeeDirectoryRow {
  readonly id: string;
  readonly achau_user_id: string;
  readonly email: string;
  readonly full_name: string;
  readonly role: EmployeeRole;
  readonly job_title: string | null;
  readonly department_id: string | null;
  readonly department_name: string | null;
  readonly branch_id: string | null;
  readonly branch_name: string | null;
  readonly manager_employee_id: string | null;
  readonly is_active: boolean;
  readonly offboarded_at: string | null;
  readonly synced_at: string;
}

/** `EmployeeDirectoryList` — `{ employees, total, limit, offset }`, offset-paged. */
export interface EmployeeDirectoryList {
  readonly employees: readonly EmployeeDirectoryRow[];
  readonly total: number;
  readonly limit: number;
  readonly offset: number;
}

/** `EmployeeManagerBrief` — `null` when the employee has no manager on record. */
export interface EmployeeManagerBrief {
  readonly id: string;
  readonly full_name: string;
}

/**
 * `EmployeeProfile` — `EmployeeDirectoryRow` plus the manager brief and the
 * direct-report count. `readEmployee`'s own description: `404 WORKFORCE_NOT_FOUND`
 * both when the row does not exist and when it sits outside the caller's resolved
 * scope, deliberately the same code either way.
 */
export interface EmployeeProfile extends EmployeeDirectoryRow {
  readonly manager: EmployeeManagerBrief | null;
  readonly direct_reports_count: number;
}

/** `EmployeeDepartmentFacet` — one row of the department filter dropdown. */
export interface EmployeeDepartmentFacet {
  readonly department_id: string | null;
  readonly department_name: string | null;
  readonly active_count: number;
}

/** `EmployeeDepartmentFacetList` — `{ departments }`, no pagination. */
export interface EmployeeDepartmentFacetList {
  readonly departments: readonly EmployeeDepartmentFacet[];
}

export const EMPLOYEES_PATH = '/api/v1/employees';
export const EMPLOYEE_DEPARTMENTS_PATH = `${EMPLOYEES_PATH}/departments`;

/** The filters `GET /employees` accepts, camelCased for the caller. */
export interface EmployeesQuery {
  readonly q?: string | undefined;
  readonly status?: EmployeeStatusFilter | undefined;
  readonly departmentId?: string | undefined;
  readonly role?: EmployeeRole | undefined;
  readonly managerEmployeeId?: string | undefined;
  readonly sort?: EmployeeSort | undefined;
  readonly limit?: number | undefined;
  readonly offset?: number | undefined;
}

/**
 * `GET /employees?q=&status=&department_id=&role=&manager_employee_id=&sort=&limit=&offset=`.
 *
 * Every member is a **server** filter, intersected with the caller's resolved
 * scope — never applied client-side over a page already in hand (`presence.ts`'s
 * own rule for every narrowing in this file). An omitted field is an omitted
 * parameter (`qs` drops `undefined`), so the endpoint's own defaults (`status=active`,
 * `sort=full_name`) apply exactly when the caller passes nothing.
 */
export function employeesPath(query: EmployeesQuery = {}): string {
  const params: Readonly<Record<string, QueryValue>> = {
    q: query.q,
    status: query.status,
    department_id: query.departmentId,
    role: query.role,
    manager_employee_id: query.managerEmployeeId,
    sort: query.sort,
    limit: query.limit,
    offset: query.offset,
  };
  return `${EMPLOYEES_PATH}${qs(params)}`;
}

/** `GET /employees/{employeeId}` — one profile. */
export function employeePath(employeeId: string): string {
  return `${EMPLOYEES_PATH}/${encodeURIComponent(employeeId)}`;
}

/**
 * `GET /employees/departments` — the department facet, over the caller's resolved,
 * ACTIVE rows. No filters of its own: `listEmployeeDepartments`'s only parameter is
 * `CorrelationId`.
 */
export function employeeDepartmentsPath(): string {
  return EMPLOYEE_DEPARTMENTS_PATH;
}
